import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/accordion'
import { Box, Flex, Link, ListItem, VStack } from '@chakra-ui/layout'
import {
  chakra,
  Icon,
  StackDivider,
  Text as ChakraText,
  UnorderedList,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { HiLightBulb } from 'react-icons/hi'
import { IoExtensionPuzzleOutline } from 'react-icons/io5'

const TextLink = chakra(Link, {
  baseStyle: {
    lineHeight: 'base',
  },
})

const origin: {
  [originName: string]: {
    title: string
    link: string
  }
} = {
  amazon_s3_bucket: {
    title: 'Amazon S3 bucket origin',
    link: 'https://docs.imagekit.io/integration/configure-origin/amazon-s3-bucket-origin',
  },
  s3_compatible_storage: {
    title: 'S3-Compatible storages',
    link: 'https://docs.imagekit.io/integration/configure-origin/s3-compatible-external-storages',
  },
  ali_storage: {
    title: 'Ali Storage',
    link: 'https://docs.imagekit.io/integration/configure-origin/alibaba-object-storage-service',
  },
  wasabi_storage: {
    title: 'Wasabi Storage',
    link: 'https://docs.imagekit.io/integration/configure-origin/wasabi-storage',
  },
  digital_ocean_spaces: {
    title: 'Digital Ocean Spaces',
    link: 'https://docs.imagekit.io/integration/configure-origin/digital-ocean-spaces',
  },
  web_server: {
    title: 'web server origin',
    link: 'https://docs.imagekit.io/integration/configure-origin/web-server-origin',
  },
  web_proxy: {
    title: 'Web proxy',
    link: 'https://docs.imagekit.io/integration/configure-origin/web-proxy',
  },
  azure_blob_storage: {
    title: 'Azure Blob storage',
    link: 'https://docs.imagekit.io/integration/configure-origin/azure-blob-storage',
  },
  google_storage: {
    title: 'Google Storage',
    link: 'https://docs.imagekit.io/integration/configure-origin/google-cloud-storage',
  },
  firebase_storage: {
    title: 'Firebase Storage',
    link: 'https://docs.imagekit.io/integration/configure-origin/firebase-storage',
  },
  cloudinary_backup_bucket: {
    title: 'Cloudinary backup bucket',
    link: 'https://docs.imagekit.io/integration/configure-origin/cloudinary-backup-bucket',
  },
  akeneo_pim: {
    title: 'Akeneo PIM',
    link: 'https://docs.imagekit.io/integration/configure-origin/akeneo',
  },
}

const DocumentationIntegrationBasics = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <AccordionItem borderTop="none">
      <AccordionButton px={12} py={8}>
        <Flex flex="1" textAlign="left" fontWeight="bold">
          <Icon as={IoExtensionPuzzleOutline} boxSize={6} mr={4} />
          {t('integration_basics')}
        </Flex>
        <Box background="appPaleGreyThree" borderRadius="full" padding={1}>
          <AccordionIcon color="appSlate" display="block" />
        </Box>
      </AccordionButton>

      <AccordionPanel px={12} py={8}>
        <VStack spacing={3} divider={<StackDivider borderColor="gray.200" />}>
          <Flex direction="column" width="100%">
            <ChakraText fontWeight="semibold" mb={4}>
              {t('support_quick_overview')}
            </ChakraText>
            <ChakraText>{t('support_quick_overview_description')}</ChakraText>
          </Flex>

          <Flex direction="column" width="100%">
            <ChakraText fontWeight="semibold" mb={8}>
              {t('support_step_1')}
            </ChakraText>
            <Flex backgroundColor="appWhiteSmoke" pl={7} mb={6} mt={4}>
              <Icon
                boxSize={7}
                borderRadius={3}
                backgroundColor="appPale"
                color="appYellowOrange"
                as={HiLightBulb}
                mr={4}
                mt={5}
              />

              <Flex direction="column">
                <ChakraText mb={2} fontWeight="semibold" mt={5}>
                  {t('support_step_1_recommend')}
                </ChakraText>
                <ChakraText mb={4}>
                  {t('support_step_1_recommend_description')}
                </ChakraText>
              </Flex>
            </Flex>
            <ChakraText fontWeight="semibold" mb={4}>
              {t('support_step_1_imagekit_support')}
            </ChakraText>
            <UnorderedList paddingLeft={5}>
              {Object.keys(origin).map(key => {
                const obj = origin[key]
                return key !== 'web_server' ? (
                  <ListItem key={obj.title}>
                    <Link isExternal={true} key={obj.title} href={obj.link}>
                      {obj.title}
                    </Link>
                  </ListItem>
                ) : (
                  <ListItem key={origin.web_server.title}>
                    <ChakraText>
                      Any{' '}
                      <Link
                        isExternal
                        key={origin.web_server.title}
                        href={origin.web_server.link}
                      >
                        {origin.web_server.title}
                      </Link>{' '}
                      , for example - Magento, WordPress, etc.
                    </ChakraText>
                  </ListItem>
                )
              })}
            </UnorderedList>
            <Link />
          </Flex>

          <Flex direction="column" width="100%">
            <ChakraText fontWeight="semibold" mb={8}>
              {t('support_step_2')}
            </ChakraText>
            <ChakraText mb={4}>{t('support_step_2_description')}</ChakraText>
            <ChakraText fontWeight="500" mb={4}>
              {t('support_step_2_media_library_allows')}
            </ChakraText>
            <ChakraText pl={10} mb={4}>
              -{' '}
              {t(
                'upload_files_via_dashboard',
                'Upload files via the ImageKit.io dashboard.',
              )}
              <br />-{' '}
              <Trans t={t} i18nKey="create_delete_folders_description">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/media-library/overview/folders"
                >
                  Create folders
                </TextLink>{' '}
                and{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/media-library/overview/folders"
                >
                  Delete folders
                </TextLink>{' '}
                to manage your images.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="tag_files_description">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/media-library/overview/image-tags"
                >
                  Tag files
                </TextLink>{' '}
                for a better organization.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="search_files_description">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/media-library/overview/search-update-and-delete"
                >
                  Search files
                </TextLink>{' '}
                by name, folder name, and tags.
              </Trans>
              <br />-{' '}
              {t(
                'delete_selected_files',
                'Delete selected files or an entire folder.',
              )}
              <br />- {t('bulk_add_or_remove', 'Bulk add or remove tags.')}
              <br />- {t('copy_or_move_file', 'Copy or move files.')}
            </ChakraText>
            <ChakraText mb={4}>
              {t('support_step_2_all_media_library_operations')}
            </ChakraText>
            <ChakraText pl={10} lineHeight={1}>
              -{' '}
              <Trans t={t} i18nKey="upload_files_from_server_or_client">
                {' '}
                Upload files from the{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/upload-file-api/server-side-file-upload"
                >
                  {' '}
                  server
                </TextLink>{' '}
                or{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/upload-file-api/client-side-file-upload"
                >
                  client-side.
                </TextLink>
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="updating_file_details">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/update-file-details"
                >
                  Updating files{' '}
                </TextLink>
                details, e.g., tags and custom coordinates for cropping.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="listing_and_searching">
                {' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/list-and-search-files"
                >
                  Listing
                </TextLink>{' '}
                and{' '}
                <TextLink isExternal href="">
                  searching files
                </TextLink>{' '}
                in the Media library by file/folder name, tags.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="getting_individual_files">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/get-file-details"
                >
                  Getting individual file{' '}
                </TextLink>{' '}
                details.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="deleting_files">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/delete-file"
                >
                  Deleting files.
                </TextLink>{' '}
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="deleting_files_bulk">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/delete-files-bulk"
                >
                  Delete files in bulk
                </TextLink>
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="purging_cache_on_cdn">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/purge-cache"
                >
                  Purging cache
                </TextLink>{' '}
                on CDN.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="getting_image_metadata">
                {' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/metadata-api/get-image-metadata-for-uploaded-media-files"
                >
                  Getting image metadata
                </TextLink>
                for an image file.{' '}
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="bulk_add_or_remove_tags">
                {' '}
                Bulk{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/add-tags-bulk"
                >
                  add{' '}
                </TextLink>
                or{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/remove-tags-bulk"
                >
                  remove
                </TextLink>{' '}
                tags.{' '}
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="copy_or_move_files">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/copy-file"
                >
                  Copy
                </TextLink>{' '}
                or{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/move-file"
                >
                  move
                </TextLink>{' '}
                files.
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="copy_or_move_folders">
                {' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/copy-folder"
                >
                  Copy
                </TextLink>{' '}
                or{' '}
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/move-folder"
                >
                  move
                </TextLink>{' '}
                folder.{' '}
              </Trans>
              <br />-{' '}
              <Trans t={t} i18nKey="get_bulk_status">
                <TextLink
                  isExternal
                  href="https://docs.imagekit.io/api-reference/media-api/copy-move-folder-status"
                >
                  Get bulk job status
                </TextLink>{' '}
                for copy and move folder jobs.
              </Trans>
              <br />
            </ChakraText>
          </Flex>
        </VStack>
      </AccordionPanel>
    </AccordionItem>
  )
}

export default DocumentationIntegrationBasics
